<!-- 编辑歌曲详情弹窗 -->
<script setup lang="ts">
import { watch, ref } from 'vue'
import { editSongDetail, universalUpload } from '~/apis/app'
import { useToastHandler } from '~/composables/useToastHandler'
import Loading from '~/assets/icons/loading.svg?component'

const { showErrorToast } = useToastHandler()

// 定义 song 类型
type Song = {
  uuid?: string
  title?: string
  fileInfo?: {
    cosUrl?: string
    mp3Url?: string
    mp4Url?: string
    width?: number
    height?: number
    size?: number
    duration?: number
    filename?: string
  }
  metadata?: {
    prompt?: string
    tags?: string
    type?: string
    duration?: number
    [key: string]: any
  }
  fullPrompt?: {
    prompt?: string
    lyrics?: string
    url?: string
    [key: string]: any
  }
  [key: string]: any
}

const props = defineProps<{
  open: boolean
  song?: Song | null
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'saved', payload: { title: string, lyrics: string }): void
}>()

const internalOpen = ref(props.open)

const formatDate = (value?: string | number | Date | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

// 从 song 对象解析数据
const parseSongData = (song: Song) => {
  // 解析标题
  const title = song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' ? song.fullPrompt?.title : (song.title || song.metadata?.prompt || `Untitled - ${formatDate(song.createdAt)}`) || ''

  // 解析歌词
  const lyrics = song.metadata?.prompt || ''

  // 解析封面和 cosUrl
  const coverUrl = song.fileInfo?.cosUrl
  const cosUrl = song.fileInfo?.cosUrl

  return { title, lyrics, coverUrl, cosUrl }
}

const formTitle = ref('')
const formLyrics = ref('')
const formCoverUrl = ref('')
const formCosUrl = ref('')
const isSaving = ref(false)
const isUploading = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)

const syncForm = () => {
  const data = parseSongData(props.song)
  formTitle.value = data.title
  formLyrics.value = data.lyrics
  formCoverUrl.value = data.coverUrl
  formCosUrl.value = data.cosUrl
}

watch(() => props.open, (isOpen) => {
  internalOpen.value = isOpen
  if (isOpen) {
    syncForm()
  }
})

watch(internalOpen, (value) => {
  emit('update:open', value)
})

watch(
  () => [props.song],
  () => {
    if (!props.open) return
    syncForm()
  }
)

const titleLimit = 80
const lyricsLimit = 7500

const handleClose = () => {
  if (isSaving.value) return
  internalOpen.value = false
}

const handleSave = async () => {
  if (!formCosUrl.value) {
    emit('update:open', false)
    return
  }

  // 获取 uuid
  const uuid = props.song?.uuid
  if (!uuid) {
    showErrorToast('Song UUID is required')
    return
  }

  if (isSaving.value) return
  isSaving.value = true
  try {
    await editSongDetail(uuid, {
      cosUrl: formCosUrl.value,
      title: formTitle.value.trim(),
      prompt: formLyrics.value
    })
    emit('saved', {
      title: formTitle.value.trim(),
      lyrics: formLyrics.value
    })
    internalOpen.value = false
  } finally {
    isSaving.value = false
  }
}

const handleReplaceCover = () => {
  fileInputRef.value?.click()
}

const handleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) {
    return
  }

  if (!file.type.startsWith('image/')) {
    showErrorToast('Please select an image file')
    return
  }

  const maxSize = 5 * 1024 * 1024
  if (file.size > maxSize) {
    showErrorToast('Image size should be less than 5MB')
    return
  }

  try {
    isUploading.value = true
    const response = await universalUpload({
      sex: '1',
      resize: '1',
      fileType: 'image',
      accelerate: '1'
    }, file)
    if (response.success && response.data) {
      const url = response.data as string
      formCoverUrl.value = url
      formCosUrl.value = url
    } else {
      showErrorToast(response.message || 'Upload failed')
    }
  } catch (error: any) {
    showErrorToast(error, 'Upload failed, please try again')
  } finally {
    isUploading.value = false
    if (target) {
      target.value = ''
    }
  }
}
</script>

<template>
  <UModal
    v-model:open="internalOpen"
    :ui="{
      overlay: 'bg-[#101012B2] backdrop-blur-[3px]',
      content: 'w-[calc(100%-32px)] md:w-[640px] bg-[#2D2D39] rounded-[16px] ring-[none]'
    }"
  >
    <template #content>
      <div class="relative flex flex-col gap-y-[16px] md:gap-y-[24px] w-full h-full font-['Roboto'] p-[32px] overflow-hidden">
        <!-- 关闭按钮 -->
        <button
          class="absolute top-[16px] right-[16px] flex items-center justify-center w-[34px] h-[34px] cursor-pointer before:content-[''] before:absolute before:w-[20px] before:h-[2px] before:bg-[#8888A1] before:rotate-45 before:rounded-[20px] after:content-[''] after:absolute after:w-[20px] after:h-[2px] after:bg-[#8888A1] after:-rotate-45 after:rounded-[20px] hover:before:bg-[#E7E7EC] hover:after:bg-[#E7E7EC] transition-colors"
          @click="handleClose"
        />

        <h2 class="text-[24px] text-white leading-[28px] font-semibold">
          Edit song's details
        </h2>

        <div class="flex flex-col md:flex-row gap-[24px] overflow-y-auto">
          <div class="flex flex-col items-center gap-[8px]">
            <div class="relative w-[160px] h-[160px] rounded-[12px] overflow-hidden bg-[#1A1A24] border border-[#5E5E77] flex items-center justify-center">
              <img
                v-if="formCoverUrl"
                :src="formCoverUrl"
                alt="cover"
                class="w-full h-full object-cover"
              >
              <div
                v-else
                class="text-[12px] text-white text-center"
              >
                No cover
              </div>
              <div
                v-if="isUploading"
                class="absolute inset-0 bg-[#0F0F18]/60 flex items-center justify-center"
              >
                <Loading class="size-[24px] text-[#E7E7EC] animate-spin" />
              </div>
            </div>
            <button
              class="px-[16px] py-[6px] text-[12px] text-[#E7E7EC] bg-[#383847] rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isUploading"
              @click="handleReplaceCover"
            >
              {{ isUploading ? 'Uploading...' : 'Replace' }}
            </button>
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleFileChange"
            >
          </div>

          <div class="flex-1 flex flex-col gap-[16px]">
            <div class="flex flex-col gap-[8px]">
              <label class="text-[14px] text-[#E7E7EC] leading-[20px]">
                Displayed Title
              </label>
              <UInput
                v-model="formTitle"
                maxlength="80"
                class="w-full"
                :ui="{
                  base: 'w-full text-[16px] md:text-[14px] text-[#E7E7EC] bg-[#1A1A24] border border-[#5E5E77] rounded-[8px] px-[14px] py-[10px]'
                }"
              />
              <p class="text-[12px] text-[#5E5E77] text-right">
                {{ formTitle?.length }}/{{ titleLimit }}
              </p>
            </div>

            <div class="flex flex-col gap-[8px]">
              <label class="text-[14px] text-[#E7E7EC] leading-[20px]">
                Edit Displayed Lyrics
              </label>
              <UTextarea
                v-model="formLyrics"
                :maxlength="lyricsLimit"
                :ui="{
                  base: 'min-h-[220px] text-[16px] md:text-[14px] text-[#E7E7EC] bg-[#1A1A24] border border-[#5E5E77] rounded-[8px] px-[14px] py-[10px]'
                }"
              />
              <p class="text-[12px] text-[#5E5E77] text-right">
                {{ formLyrics.length }}/{{ lyricsLimit }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <button
            class="min-w-[120px] px-[24px] py-[10px] rounded-[8px] text-[14px] text-white bg-gradient-to-r from-[#CB2DFF] to-[#6C63FF] disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isSaving || !formCosUrl"
            @click="handleSave"
          >
            {{ isSaving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </template>
  </UModal>
</template>
